import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Routes } from "../routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faTimes,
  faCalendar,
  faMapMarkerAlt,
  faUsers,
  faCloudUploadAlt,
  faPlus,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Col,
  Row,
  Button,
  Table,
  Spinner,
  Form,
  Modal,
  Badge,
} from "@themesberg/react-bootstrap";
import {
  PutApiRequest,
  DeleteApiRequest,
  PostApiRequest,
  resolveImageUrl,
  placeholderImageDataUrl,
} from "../services/api";
import {
  fetchPendingExperiences,
  setCurrentPage,
  clearError,
} from "../redux/slices/pendingExperiencesSlice";
import { toast } from "../components/Toaster";

const PENDING_THUMB_PLACEHOLDER = placeholderImageDataUrl(60, 60);

const PendingExperiencesPage = ({ createOnly = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    experiences,
    loading,
    error,
    pagination,
    currentPage,
    limit,
    hasLoaded,
    lastFetchedPage,
    lastFetchedLimit,
  } = useSelector((state) => state.pendingExperiences);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const TOTAL_CREATE_STEPS = 4;
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [tagInput, setTagInput] = useState("");
  const [footerStyle, setFooterStyle] = useState({});
  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    images: [],
    fileUploadIds: [],
    category: "",
    price: "Free",
    grouptype: [],
    location: {
      lat: "",
      lng: "",
      address: "",
    },
    tags: [],
    phone: "",
    website: "",
    openingHours: {
      monday: { open: "", close: "", closed: false },
      tuesday: { open: "", close: "", closed: false },
      wednesday: { open: "", close: "", closed: false },
      thursday: { open: "", close: "", closed: false },
      friday: { open: "", close: "", closed: false },
      saturday: { open: "", close: "", closed: false },
      sunday: { open: "", close: "", closed: false },
    },
    hoursType: "",
    timezone: "",
    specialEventHours: [{ date: "", startTime: "", endTime: "" }],
    bookingLink: "",
    bookingProvider: "golocal",
    viatorProductCode: "",
  });

  const categories = [
    "Food",
    "Events",
    "Outdoors",
    "Nightlife",
    "Wellness",
    "Art",
    "Hidden Gems",
    "Family",
  ];

  const USA_TIMEZONES = [
    { id: "America/New_York", name: "Eastern Time (New York)" },
    { id: "America/Chicago", name: "Central Time (Chicago)" },
    { id: "America/Denver", name: "Mountain Time (Denver)" },
    { id: "America/Phoenix", name: "Arizona (Phoenix, no DST)" },
    { id: "America/Los_Angeles", name: "Pacific Time (Los Angeles)" },
    { id: "America/Anchorage", name: "Alaska (Anchorage)" },
    { id: "America/Honolulu", name: "Hawaii (Honolulu)" },
    { id: "America/Detroit", name: "Eastern - Detroit" },
    { id: "America/Boise", name: "Mountain - Boise" },
    {
      id: "America/Indiana/Indianapolis",
      name: "Eastern - Indiana (Indianapolis)",
    },
    {
      id: "America/Kentucky/Louisville",
      name: "Eastern - Kentucky (Louisville)",
    },
    { id: "America/Menominee", name: "Central - Menominee" },
    { id: "America/North_Dakota/Center", name: "Central - North Dakota" },
    { id: "America/Adak", name: "Alaska - Adak (Aleutian)" },
    { id: "America/Juneau", name: "Alaska - Juneau" },
    { id: "America/Nome", name: "Alaska - Nome" },
    { id: "America/Sitka", name: "Alaska - Sitka" },
    { id: "America/Yakutat", name: "Alaska - Yakutat" },
  ];

  useEffect(() => {
    const updateFooterBounds = () => {
      const main = document.querySelector("main.content");
      if (main) {
        const rect = main.getBoundingClientRect();
        setFooterStyle({
          position: "fixed",
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          bottom: 0,
          background: "#ffffff",
          borderTop: "1px solid #e5e7eb",
          padding: "0.75rem 1rem",
          zIndex: 1030,
        });
      }
    };
    updateFooterBounds();
    window.addEventListener("resize", updateFooterBounds);
    return () => window.removeEventListener("resize", updateFooterBounds);
  }, []);

  // Fetch pending experiences on mount if not already loaded (smart caching)
  useEffect(() => {
    if (createOnly) return;
    if (!hasLoaded) {
      dispatch(fetchPendingExperiences({ page: currentPage, limit }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOnly]);

  useEffect(() => {
    if (createOnly) return;
    const shouldRefetch =
      hasLoaded &&
      (currentPage !== lastFetchedPage || limit !== lastFetchedLimit);
    if (shouldRefetch) {
      dispatch(fetchPendingExperiences({ page: currentPage, limit }));
    }
  }, [
    createOnly,
    currentPage,
    hasLoaded,
    dispatch,
    limit,
    lastFetchedPage,
    lastFetchedLimit,
  ]);

  // Show error toast if loading fails
  useEffect(() => {
    if (error && hasLoaded) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch, hasLoaded]);

  const handleApprove = async (experienceId) => {
    setActionLoading(true);
    try {
      await PutApiRequest(`admin/experiences/${experienceId}/approve`, {});
      toast.success("Experience approved successfully!");
      // Refetch after approval
      dispatch(fetchPendingExperiences({ page: currentPage, limit }));
    } catch (error) {
      console.error("Error approving experience:", error);
      toast.error(
        error?.response?.data?.message || "Failed to approve experience",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setActionLoading(true);
    try {
      await PutApiRequest(
        `admin/experiences/${selectedExperience._id}/reject`,
        {
          reason: rejectReason,
        },
      );
      toast.success("Experience rejected successfully!");
      setShowRejectModal(false);
      setRejectReason("");
      setSelectedExperience(null);
      // Refetch after rejection
      dispatch(fetchPendingExperiences({ page: currentPage, limit }));
    } catch (error) {
      console.error("Error rejecting experience:", error);
      toast.error(
        error?.response?.data?.message || "Failed to reject experience",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await DeleteApiRequest(`admin/experiences/${selectedExperience._id}`);
      toast.success("Experience removed from public view");
      setShowDeleteModal(false);
      setSelectedExperience(null);
      // Refetch after deletion
      dispatch(fetchPendingExperiences({ page: currentPage, limit }));
    } catch (error) {
      console.error("Error deleting experience:", error);
      toast.error(
        error?.response?.data?.message || "Failed to remove experience",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const uploadImages = async (files) => {
    if (!files || files.length === 0) {
      toast.error("No image file provided");
      return;
    }

    setUploadingImages(true);
    try {
      const formDataUpload = new FormData();
      for (let i = 0; i < files.length; i++) {
        formDataUpload.append("images", files[i]);
      }
      const response = await PostApiRequest(
        "images/upload-multiple",
        formDataUpload,
      );
      const imagesData = response?.data?.data?.images || [];
      const uploadedIds = imagesData.map((img) => img.uploadId).filter(Boolean);
      const previewUrls = imagesData.map((img) => img.url).filter(Boolean);
      const urls =
        previewUrls.length === uploadedIds.length
          ? previewUrls
          : Array.from(files).map((f) => URL.createObjectURL(f));

      if (uploadedIds.length > 0) {
        setCreateForm((prev) => ({
          ...prev,
          images: [...prev.images, ...urls],
          fileUploadIds: [...prev.fileUploadIds, ...uploadedIds],
        }));
        toast.success(`${uploadedIds.length} image(s) uploaded successfully!`);
      } else {
        toast.error("No images were uploaded");
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error(error?.response?.data?.message || "Failed to upload images");
    } finally {
      setUploadingImages(false);
    }
  };

  const handleImageUpload = (e) => {
    uploadImages(e.target.files);
  };

  const handleRemoveImage = (index) => {
    setCreateForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      fileUploadIds: prev.fileUploadIds.filter((_, i) => i !== index),
    }));
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} - ${digits.slice(6)}`;
  };

  const isValidWebsiteUrl = (value) => {
    if (!value || !value.trim()) return true;
    if (/^https?:\/\/.+\..+/.test(value)) return true;
    if (/^www\..+\..+/.test(value)) return true;
    if (
      /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}(\/.*)?$/.test(value)
    )
      return true;
    return false;
  };

  const isValidBookingUrl = (value) => {
    if (!value || !value.trim()) return true;
    if (/^https?:\/\/.+\..+/.test(value)) return true;
    if (/^www\..+\..+/.test(value)) return true;
    if (
      /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}(\/.*)?$/.test(value)
    )
      return true;
    return false;
  };

  const validateStep = (step) => {
    const errors = {};
    if (step === 1) {
      if (!createForm.title.trim()) errors.title = "Title is required";
      else if (createForm.title.length < 3)
        errors.title = "Title must be at least 3 characters";
      if (!createForm.description.trim())
        errors.description = "Description is required";
      else if (createForm.description.length < 10)
        errors.description = "Description must be at least 10 characters";
      if (!createForm.category) errors.category = "Category is required";
      if (
        createForm.price &&
        !["Free", "$", "$$", "$$$"].includes(createForm.price) &&
        !/^\$+$/.test(createForm.price) &&
        !/^\d+\$?$/.test(createForm.price)
      ) {
        errors.price = "Invalid price. Choose Free, $, $$, $$$";
      }
    }
    if (step === 2) {
      if (
        createForm.fileUploadIds.length === 0 &&
        createForm.images.length === 0
      )
        errors.images = "At least one image is required";
      if (!createForm.location.address?.trim())
        errors.address = "Address is required";
      if (
        createForm.location.lat &&
        (isNaN(parseFloat(createForm.location.lat)) ||
          createForm.location.lat < -90 ||
          createForm.location.lat > 90)
      )
        errors.lat = "Latitude must be between -90 and 90";
      if (
        createForm.location.lng &&
        (isNaN(parseFloat(createForm.location.lng)) ||
          createForm.location.lng < -180 ||
          createForm.location.lng > 180)
      )
        errors.lng = "Longitude must be between -180 and 180";
    }
    if (step === 3) {
      if (createForm.phone && createForm.phone.trim()) {
        const digitsOnly = createForm.phone.replace(/\D/g, "");
        if (digitsOnly.length !== 10)
          errors.phone = "Please enter a valid 10-digit US phone number";
      }
      if (
        createForm.website &&
        createForm.website.trim() &&
        !isValidWebsiteUrl(createForm.website)
      ) {
        errors.website =
          "Please enter a valid website URL (e.g. https://example.com or www.example.com)";
      }
      if (
        createForm.bookingLink &&
        createForm.bookingLink.trim() &&
        !isValidBookingUrl(createForm.bookingLink)
      ) {
        errors.bookingLink =
          "Please enter a valid booking URL (e.g. https://booking.example.com)";
      }
      if (
        createForm.bookingProvider === "viator" &&
        (!createForm.viatorProductCode || !createForm.viatorProductCode.trim())
      ) {
        errors.viatorProductCode =
          "Viator Product Code is required when Booking Provider is Viator";
      }
    }
    if (step === 4) {
      if (
        createForm.hoursType === "regular" ||
        createForm.hoursType === "special"
      ) {
        if (!createForm.timezone || !createForm.timezone.trim()) {
          errors.timezone = "Please select a business timezone (USA)";
        }
      }
      if (createForm.hoursType === "special") {
        const valid = createForm.specialEventHours.some(
          (e) => e.date && e.startTime,
        );
        if (!valid)
          errors.specialEventHours =
            "Add at least one event with date and start time";
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateNext = () => {
    if (validateStep(createStep)) {
      setCreateStep((prev) => Math.min(prev + 1, TOTAL_CREATE_STEPS));
    } else {
      toast.error("Please fix the errors on this step");
    }
  };

  const handleCreatePrev = () => {
    setCreateStep((prev) => Math.max(prev - 1, 1));
    setValidationErrors({});
  };

  const handleCreateExperience = async () => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      toast.error("Please complete steps 1–3 and fix any errors");
      return;
    }
    if (createForm.hoursType && !validateStep(4)) {
      toast.error(
        createForm.hoursType === "special"
          ? "Please add at least one special event and select a timezone"
          : "Please select a business timezone (USA)",
      );
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        title: createForm.title,
        description: createForm.description,
        category: createForm.category,
        price: createForm.price === "Free" ? "FREE" : createForm.price,
        grouptype: createForm.grouptype,
        location: {
          lat: parseFloat(createForm.location.lat) || 0,
          lng: parseFloat(createForm.location.lng) || 0,
          address: createForm.location.address || "",
        },
        tags: createForm.tags,
        phone: createForm.phone,
        website: createForm.website,
        bookingLink: createForm.bookingLink,
        bookingProvider: createForm.bookingProvider || "golocal",
        ...(createForm.bookingProvider === "viator" && {
          viatorProductCode: createForm.viatorProductCode,
        }),
      };
      if (createForm.fileUploadIds?.length > 0) {
        payload.fileUploadIds = createForm.fileUploadIds;
      } else if (createForm.images?.length > 0) {
        payload.images = createForm.images;
      }
      if (createForm.hoursType === "regular") {
        payload.hoursType = "regular";
        payload.timezone =
          (createForm.timezone || "").trim() || "America/New_York";
        // Match app/backend: open/close as "HH:mm" or null, closed boolean (no empty strings)
        const toHHmm = (val) => {
          if (val == null || val === "") return null;
          const s = String(val).trim();
          if (/^\d{1,2}:\d{2}$/.test(s)) return s;
          const d = new Date(val);
          if (Number.isNaN(d.getTime())) return null;
          const h = d.getHours();
          const m = d.getMinutes();
          return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
        };
        const DAYS = [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ];
        payload.openingHours = Object.fromEntries(
          DAYS.map((day) => {
            const row = createForm.openingHours?.[day] ?? {
              open: "",
              close: "",
              closed: true,
            };
            const closed = !!row.closed;
            const open = closed ? null : toHHmm(row.open);
            const close = closed ? null : toHHmm(row.close);
            return [day, { closed, open, close }];
          }),
        );
      } else if (createForm.hoursType === "special") {
        const validEvents = createForm.specialEventHours
          .filter((e) => e.date && e.startTime)
          .map((e) => ({
            date: new Date(e.date).toISOString(),
            startTime: /^\d{1,2}:\d{2}$/.test(String(e.startTime))
              ? e.startTime
              : `${String(new Date(e.startTime).getHours()).padStart(2, "0")}:${String(new Date(e.startTime).getMinutes()).padStart(2, "0")}`,
            endTime:
              e.endTime && /^\d{1,2}:\d{2}$/.test(String(e.endTime))
                ? e.endTime
                : e.endTime
                  ? `${String(new Date(e.endTime).getHours()).padStart(2, "0")}:${String(new Date(e.endTime).getMinutes()).padStart(2, "0")}`
                  : null,
          }));
        if (validEvents.length > 0) {
          payload.hoursType = "special";
          payload.timezone =
            (createForm.timezone || "").trim() || "America/New_York";
          payload.specialEventHours = validEvents;
        }
      }

      await PostApiRequest("experiences", payload);
      toast.success("Experience created successfully!");
      setShowCreateModal(false);
      resetCreateForm();
      if (createOnly) {
        history.push(`${Routes.Experiences.path}?state=pending`);
      } else {
        dispatch(fetchPendingExperiences({ page: currentPage, limit }));
      }
    } catch (error) {
      console.error("Error creating experience:", error);

      if (error?.response?.data?.errors) {
        const backendErrors = {};
        error.response.data.errors.forEach((err) => {
          backendErrors[err.path] = err.msg;
        });
        setValidationErrors(backendErrors);
        toast.error("Please fix the validation errors");
      } else {
        toast.error(
          error?.response?.data?.message || "Failed to create experience",
        );
      }
    } finally {
      setActionLoading(false);
    }
  };

  const resetCreateForm = () => {
    setCreateStep(1);
    setCreateForm({
      title: "",
      description: "",
      images: [],
      fileUploadIds: [],
      category: "",
      price: "Free",
      grouptype: [],
      location: { lat: "", lng: "", address: "" },
      tags: [],
      phone: "",
      website: "",
      openingHours: {
        monday: { open: "", close: "", closed: false },
        tuesday: { open: "", close: "", closed: false },
        wednesday: { open: "", close: "", closed: false },
        thursday: { open: "", close: "", closed: false },
        friday: { open: "", close: "", closed: false },
        saturday: { open: "", close: "", closed: false },
        sunday: { open: "", close: "", closed: false },
      },
      hoursType: "",
      timezone: "",
      specialEventHours: [{ date: "", startTime: "", endTime: "" }],
      bookingLink: "",
      bookingProvider: "golocal",
      viatorProductCode: "",
    });
    setValidationErrors({});
    setTagInput("");
  };

  const fromIsoToTime = (isoOrHhmm) => {
    if (!isoOrHhmm) return "";
    const s = String(isoOrHhmm).trim();
    if (/^\d{1,2}:\d{2}$/.test(s)) return s;
    const d = new Date(isoOrHhmm);
    if (Number.isNaN(d.getTime())) return "";
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const mm = String(d.getUTCMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const handleOpeningHourChange = (day, field, value) => {
    setCreateForm((prev) => ({
      ...prev,
      openingHours: {
        ...prev.openingHours,
        [day]: {
          ...prev.openingHours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleGroupTypeChange = (type) => {
    setCreateForm((prev) => ({
      ...prev,
      grouptype: prev.grouptype.includes(type)
        ? prev.grouptype.filter((t) => t !== type)
        : [...prev.grouptype, type],
    }));
  };

  const addSpecialEvent = () => {
    setCreateForm((prev) => ({
      ...prev,
      specialEventHours: [
        ...prev.specialEventHours,
        { date: "", startTime: "", endTime: "" },
      ],
    }));
  };

  const updateSpecialEvent = (index, field, value) => {
    setCreateForm((prev) => ({
      ...prev,
      specialEventHours: prev.specialEventHours.map((e, i) =>
        i === index ? { ...e, [field]: value } : e,
      ),
    }));
  };

  const removeSpecialEvent = (index) => {
    setCreateForm((prev) => ({
      ...prev,
      specialEventHours: prev.specialEventHours.filter((_, i) => i !== index),
    }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= (pagination.pages || 1)) {
      dispatch(setCurrentPage(page));
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    resetCreateForm();
    if (createOnly) {
      history.push(Routes.Experiences.path);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      Food: "danger",
      Events: "primary",
      Outdoors: "success",
      Nightlife: "warning",
      Wellness: "info",
      Art: "secondary",
      "Hidden Gems": "dark",
    };
    return colors[category] || "secondary";
  };

  return (
    <>
      <style>{`
        .page-header {
          background: #ffffff;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border-left: 4px solid #4f46e5;
        }
        
        .page-header h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }
        
        .page-header p {
          color: #64748b;
          font-size: 1rem;
          margin-bottom: 0;
        }
        
        .stats-badge {
          background: #f1f5f9;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #4f46e5;
          margin-top: 1rem;
        }
        
        .experiences-table {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .experiences-table table {
          margin-bottom: 0;
        }
        
        .experiences-table thead {
          background: #f8fafc;
        }
        
        .experiences-table th {
          font-weight: 700;
          color: #1e293b;
          padding: 1rem;
          border-bottom: 2px solid #e2e8f0;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .experiences-table tbody tr {
          transition: all 0.2s ease;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .experiences-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .experiences-table td {
          padding: 1rem;
          vertical-align: middle;
          color: #475569;
        }
        
        .experience-image {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        
        .experience-title {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
          font-size: 0.95rem;
        }
        
        .experience-creator {
          color: #94a3b8;
          font-size: 0.8rem;
        }
        
        .action-btn {
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.85rem;
          border: none;
          transition: all 0.3s ease;
          margin: 0 0.25rem;
        }
        
        .action-btn.approve {
          background: #10b981;
          color: white;
        }
        
        .action-btn.approve:hover {
          background: #059669;
          transform: translateY(-1px);
        }
        
        .action-btn.reject {
          background: #f59e0b;
          color: white;
        }
        
        .action-btn.reject:hover {
          background: #d97706;
          transform: translateY(-1px);
        }
        
        .action-btn.delete {
          background: #ef4444;
          color: white;
        }
        
        .action-btn.delete:hover {
          background: #dc2626;
          transform: translateY(-1px);
        }
        
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .empty-state svg {
          font-size: 3rem;
          color: #cbd5e0;
          margin-bottom: 1rem;
        }
        
        .empty-state h3 {
          color: #475569;
          margin-bottom: 0.5rem;
        }
        
        .empty-state p {
          color: #94a3b8;
        }
        
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 4rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .modal-content {
          border-radius: 12px;
          border: none;
        }
        
        .modal-header {
          background: #4f46e5;
          color: white;
          border-radius: 12px 12px 0 0;
          padding: 1.25rem 1.5rem;
        }
        
        .modal-title {
          font-weight: 700;
          color: white;
        }
        
        .modal-header .btn-close {
          filter: brightness(0) invert(1);
        }
        
        .image-upload-zone {
          border: 2px dashed #cbd5e0;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          background: #f8fafc;
        }
        
        .image-upload-zone:hover {
          border-color: #4f46e5;
          background: #f1f5f9;
        }
        
        .image-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        
        .image-preview-item {
          position: relative;
          border-radius: 8px;
          overflow: hidden;
          aspect-ratio: 1;
        }
        
        .image-preview-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .image-remove-btn {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(239, 68, 68, 0.95);
          color: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .image-remove-btn:hover {
          background: #dc2626;
          transform: scale(1.1);
        }

        .group-type-checkbox {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        
        .group-checkbox-item {
          flex: 1;
          min-width: 120px;
          padding: 0.75rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .group-checkbox-item:hover {
          border-color: #4f46e5;
          background: #f8fafc;
        }
        
        .group-checkbox-item.selected {
          background: #eff6ff;
          border-color: #4f46e5;
        }
        
        .group-checkbox-item input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .opening-hours-section {
          background: #f8fafc;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 0.75rem;
        }

        .opening-hours-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .day-label {
          font-weight: 600;
          text-transform: capitalize;
          color: #1e293b;
        }

        .search-input {
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          padding: 0.6rem 1rem;
          transition: all 0.3s ease;
        }
        
        .search-input:focus {
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }

        .pagination-container {
          background: white;
          border-radius: 12px;
          padding: 1.25rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-top: 1.5rem;
        }
        
        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-btn {
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          transition: all 0.3s ease;
        }
        
        .page-btn:hover:not(:disabled) {
          background: #4f46e5;
          color: white;
          border-color: #4f46e5;
        }
        
        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .create-step-indicator {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding: 0 0.5rem;
        }
        .create-step-dot {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.9rem;
          background: #e2e8f0;
          color: #64748b;
          transition: all 0.3s ease;
        }
        .create-step-dot.active {
          background: #4f46e5;
          color: white;
        }
        .create-step-dot.done {
          background: #10b981;
          color: white;
        }
        .create-step-line {
          flex: 1;
          height: 3px;
          background: #e2e8f0;
          margin: 0 4px;
          max-width: 60px;
        }
        .create-step-line.done {
          background: #10b981;
        }

        .tag-chips-wrap {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        .tag-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.35rem 0.6rem;
          background: #f1f5f9;
          border-radius: 9999px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #475569;
        }
        .tag-chip-remove {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          padding: 0;
          border: none;
          background: transparent;
          color: #94a3b8;
          cursor: pointer;
          border-radius: 50%;
          transition: color 0.2s, background 0.2s;
        }
        .tag-chip-remove:hover {
          color: #ef4444;
          background: #fef2f2;
        }
      `}</style>

      {createOnly ? (
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h2>Create experience</h2>
              <p>
                Add a new experience. It stays pending until approved in
                Experiences.
              </p>
            </div>
            <Button
              variant="outline-secondary"
              className="fw-semibold"
              style={{ borderRadius: "8px" }}
              onClick={() => history.push(Routes.Experiences.path)}
            >
              Back to Experiences
            </Button>
          </div>
        </div>
      ) : (
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2>Pending Experiences</h2>
              <p>Review and approve experiences submitted by local creators</p>
              <div className="stats-badge">
                <FontAwesomeIcon icon={faUsers} />
                {pagination?.total || 0} Pending
              </div>
            </div>
            <Button
              style={{
                background: "#4f46e5",
                border: "none",
                borderRadius: "8px",
                padding: "0.75rem 1.5rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
              onClick={() => setShowCreateModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
              Create
            </Button>
          </div>
        </div>
      )}

      {/* Initial load - show nothing while fetching */}
      {!createOnly &&
        (loading && !hasLoaded ? (
        <div className="loading-container" style={{ minHeight: "300px" }}>
          {/* Silent loading on first mount */}
        </div>
      ) : loading && hasLoaded ? (
        <div className="loading-container">
          <Spinner
            animation="border"
            style={{ color: "#4f46e5", width: "3rem", height: "3rem" }}
          />
        </div>
      ) : experiences.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faCheck} />
          <h3>No pending experiences</h3>
          <p>All experiences have been reviewed</p>
        </div>
      ) : (
        <>
          <div className="experiences-table">
            <Table responsive hover>
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>Image</th>
                  <th>Title & Creator</th>
                  <th style={{ width: "120px" }}>Category</th>
                  <th style={{ width: "200px" }}>Location</th>
                  <th style={{ width: "100px" }}>Price</th>
                  <th style={{ width: "150px" }}>Group Type</th>
                  <th style={{ width: "120px" }}>Date</th>
                  <th style={{ width: "260px" }} className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {experiences.map((exp) => {
                  const rawImage =
                    exp.fileAssets?.[0]?.urls?.medium ||
                    exp.fileAssets?.[0]?.urls?.high ||
                    exp.fileAssets?.[0]?.urls?.original ||
                    exp.images?.[0];
                  const firstImageUrl = resolveImageUrl(rawImage);
                  return (
                    <tr key={exp._id}>
                      <td>
                        <img
                          src={firstImageUrl || PENDING_THUMB_PLACEHOLDER}
                          alt={exp.title}
                          className="experience-image"
                          onError={(e) => { e.target.src = PENDING_THUMB_PLACEHOLDER; }}
                        />
                      </td>
                      <td>
                        <div className="experience-title">{exp.title}</div>
                        <div className="experience-creator">
                          by {exp.createdBy?.name || "Unknown"}
                        </div>
                      </td>
                      <td>
                        <Badge
                          bg={getCategoryColor(exp.category)}
                          style={{
                            fontSize: "0.8rem",
                            padding: "0.4rem 0.75rem",
                          }}
                        >
                          {exp.category || "N/A"}
                        </Badge>
                      </td>
                      <td>
                        <div style={{ fontSize: "0.85rem" }}>
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className="me-1"
                            style={{ color: "#94a3b8" }}
                          />
                          {exp.location?.address || "N/A"}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                          {exp.price || "N/A"}
                        </div>
                      </td>
                      <td>
                        {exp.grouptype && exp.grouptype.length > 0 ? (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "0.25rem",
                            }}
                          >
                            {exp.grouptype.map((type, idx) => (
                              <Badge
                                key={idx}
                                bg="secondary"
                                style={{
                                  fontSize: "0.75rem",
                                  padding: "0.3rem 0.6rem",
                                }}
                              >
                                {type}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>
                        <div style={{ fontSize: "0.85rem" }}>
                          <FontAwesomeIcon
                            icon={faCalendar}
                            className="me-1"
                            style={{ color: "#94a3b8" }}
                          />
                          {exp.datetime
                            ? new Date(exp.datetime).toLocaleDateString()
                            : "N/A"}
                        </div>
                      </td>
                      <td className="text-center">
                        <button
                          className="action-btn approve"
                          onClick={() => handleApprove(exp._id)}
                          disabled={actionLoading}
                        >
                          Approve
                        </button>
                        <button
                          className="action-btn reject"
                          onClick={() => {
                            setSelectedExperience(exp);
                            setShowRejectModal(true);
                          }}
                          disabled={actionLoading}
                        >
                          Reject
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => {
                            setSelectedExperience(exp);
                            setShowDeleteModal(true);
                          }}
                          disabled={actionLoading}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {experiences.length > 0 && (
            <div style={footerStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontWeight: 600, color: "#475569" }}>
                  Page {currentPage} of {pagination.pages || 1} (
                  {pagination.total || 0} total experiences)
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    className="page-btn"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let start = Math.max(
                      1,
                      currentPage - Math.floor(maxVisible / 2),
                    );
                    let end = Math.min(
                      pagination.pages || 1,
                      start + maxVisible - 1,
                    );
                    if (end - start < maxVisible - 1) {
                      start = Math.max(1, end - maxVisible + 1);
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <Button
                          key={i}
                          className="page-btn"
                          style={
                            i === currentPage
                              ? {
                                  background: "#4f46e5",
                                  color: "white",
                                  borderColor: "#4f46e5",
                                }
                              : {}
                          }
                          onClick={() => handlePageChange(i)}
                        >
                          {i}
                        </Button>,
                      );
                    }
                    return pages;
                  })()}
                  <Button
                    className="page-btn"
                    disabled={currentPage >= (pagination.pages || 1)}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
          {experiences.length > 0 && <div style={{ height: "64px" }} />}
        </>
      ))}

      {/* Reject Modal */}
      <Modal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reject Experience</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem" }}>
          {selectedExperience && (
            <>
              <div
                style={{
                  background: "#fef3c7",
                  padding: "1rem",
                  borderRadius: "8px",
                  marginBottom: "1.5rem",
                  border: "1px solid #fcd34d",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    color: "#92400e",
                  }}
                >
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span style={{ fontWeight: 600 }}>
                    This will notify the creator about the rejection
                  </span>
                </div>
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <strong>Experience:</strong> {selectedExperience.title}
              </div>
              <div style={{ marginBottom: "1.5rem" }}>
                <strong>Creator:</strong>{" "}
                {selectedExperience.createdBy?.name || "Unknown"}
              </div>

              <Form.Group>
                <Form.Label style={{ fontWeight: 600 }}>
                  Reason for Rejection *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please provide a detailed reason for rejecting this experience..."
                  className="search-input"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ border: "none", padding: "1.25rem" }}>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowRejectModal(false);
              setRejectReason("");
              setSelectedExperience(null);
            }}
            disabled={actionLoading}
            style={{
              borderRadius: "8px",
              fontWeight: 600,
              padding: "0.6rem 1.25rem",
            }}
          >
            Cancel
          </Button>
          <Button
            style={{
              background: "#f59e0b",
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              padding: "0.6rem 1.25rem",
            }}
            onClick={handleReject}
            disabled={actionLoading || !rejectReason.trim()}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Rejecting...
              </>
            ) : (
              "Reject Experience"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove experience from public view</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem" }}>
          {selectedExperience && (
            <>
              <div
                style={{
                  background: "#fffbeb",
                  padding: "1.25rem",
                  borderRadius: "8px",
                  marginBottom: "1.25rem",
                  border: "1px solid #fde68a",
                }}
              >
                <h6 style={{ color: "#92400e", marginBottom: "0.75rem" }}>
                  Hide this experience
                </h6>
                <p
                  style={{
                    color: "#78350f",
                    marginBottom: 0,
                    fontSize: "0.9rem",
                  }}
                >
                  The record stays in the database. Find it under All Experiences (inactive) to approve again or edit.
                </p>
              </div>

              <div
                style={{
                  padding: "1rem",
                  background: "#f8fafc",
                  borderRadius: "8px",
                }}
              >
                <p style={{ marginBottom: "0.5rem" }}>
                  <strong>Title:</strong> {selectedExperience.title}
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Creator:</strong>{" "}
                  {selectedExperience.createdBy?.name || "Unknown"}
                </p>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ border: "none", padding: "1.25rem" }}>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedExperience(null);
            }}
            disabled={actionLoading}
            style={{
              borderRadius: "8px",
              fontWeight: 600,
              padding: "0.6rem 1.25rem",
            }}
          >
            Cancel
          </Button>
          <Button
            style={{
              background: "#ef4444",
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              padding: "0.6rem 1.25rem",
            }}
            onClick={handleDelete}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Removing…
              </>
            ) : (
              "Remove from public view"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Experience Modal - 4 steps like mobile */}
      <Modal
        show={createOnly || showCreateModal}
        onHide={handleModalClose}
        size="lg"
        centered
        scrollable
        backdrop={createOnly ? "static" : true}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Experience</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "2rem" }}>
          {/* Step indicator */}
          <div className="create-step-indicator">
            {[1, 2, 3, 4].map((step) => (
              <React.Fragment key={step}>
                {step > 1 && (
                  <div
                    className={`create-step-line ${createStep > step ? "done" : ""}`}
                  />
                )}
                <div
                  className={`create-step-dot ${createStep === step ? "active" : createStep > step ? "done" : ""}`}
                >
                  {createStep > step ? (
                    <FontAwesomeIcon icon={faCheck} />
                  ) : (
                    step
                  )}
                </div>
                {step < 4 && (
                  <div
                    className={`create-step-line ${createStep > step ? "done" : ""}`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <div
            style={{
              marginBottom: "1rem",
              fontSize: "0.9rem",
              color: "#64748b",
            }}
          >
            Step {createStep} of {TOTAL_CREATE_STEPS}:{" "}
            {createStep === 1 && "Basic Information"}
            {createStep === 2 && "Experience Details"}
            {createStep === 3 && "Additional Information"}
            {createStep === 4 && "Hours"}
          </div>

          {Object.keys(validationErrors).length > 0 && (
            <div
              style={{
                background: "#fef2f2",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                border: "1px solid #fecaca",
              }}
            >
              <strong style={{ color: "#991b1b" }}>Please fix:</strong>
              <ul
                style={{
                  marginTop: "0.5rem",
                  marginBottom: 0,
                  paddingLeft: "1.5rem",
                }}
              >
                {Object.values(validationErrors).map((err, idx) => (
                  <li key={idx} style={{ color: "#7f1d1d" }}>
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Form>
            {/* Step 1: Basic Information */}
            {createStep === 1 && (
              <div>
                <h5
                  style={{
                    marginBottom: "1rem",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  Basic Information
                </h5>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600 }}>
                        Title <span style={{ color: "#ef4444" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={createForm.title}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            title: e.target.value,
                          })
                        }
                        placeholder="Enter a catchy title"
                        isInvalid={!!validationErrors.title}
                        className="search-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.title}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600 }}>
                        Description <span style={{ color: "#ef4444" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={createForm.description}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            description: e.target.value,
                          })
                        }
                        placeholder="Describe what makes this experience special..."
                        isInvalid={!!validationErrors.description}
                        className="search-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.description}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600 }}>
                        Category <span style={{ color: "#ef4444" }}>*</span>
                      </Form.Label>
                      <Form.Select
                        value={createForm.category}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            category: e.target.value,
                          })
                        }
                        isInvalid={!!validationErrors.category}
                        className="search-input"
                      >
                        <option value="">Select category</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.category}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600 }}>Price</Form.Label>
                      <Form.Select
                        value={createForm.price}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            price: e.target.value,
                          })
                        }
                        isInvalid={!!validationErrors.price}
                        className="search-input"
                      >
                        <option value="Free">Free</option>
                        <option value="$">$</option>
                        <option value="$$">$$</option>
                        <option value="$$$">$$$</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.price}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}

            {/* Step 2: Experience Details - Images, Suitable For, Address */}
            {createStep === 2 && (
              <div>
                <h5
                  style={{
                    marginBottom: "1rem",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  Experience Details
                </h5>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>
                    Upload Images <span style={{ color: "#ef4444" }}>*</span>
                  </Form.Label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                    disabled={uploadingImages}
                  />
                  <div
                    className="image-upload-zone"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      uploadImages(e.dataTransfer.files);
                    }}
                    style={
                      validationErrors.images ? { borderColor: "#ef4444" } : {}
                    }
                  >
                    <FontAwesomeIcon
                      icon={faCloudUploadAlt}
                      style={{
                        fontSize: "2rem",
                        color: "#4f46e5",
                        marginBottom: "0.5rem",
                      }}
                    />
                    <div style={{ fontWeight: 600 }}>
                      Click or drag and drop
                    </div>
                    <small style={{ color: "#64748b" }}>
                      PNG, JPG (multiple supported)
                    </small>
                  </div>
                  {validationErrors.images && (
                    <div
                      style={{
                        color: "#ef4444",
                        fontSize: "0.875rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      {validationErrors.images}
                    </div>
                  )}
                  {uploadingImages && (
                    <div
                      style={{
                        marginTop: "1rem",
                        textAlign: "center",
                        color: "#4f46e5",
                      }}
                    >
                      <Spinner animation="border" size="sm" className="me-2" />
                      Uploading...
                    </div>
                  )}
                  {createForm.images.length > 0 && (
                    <div
                      className="image-preview-grid"
                      style={{ marginTop: "1rem" }}
                    >
                      {createForm.images.map((img, idx) => (
                        <div key={idx} className="image-preview-item">
                          <img src={img} alt={`Preview ${idx + 1}`} />
                          <button
                            type="button"
                            className="image-remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage(idx);
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    Suitable For
                  </Form.Label>
                  <div className="group-type-checkbox">
                    {["solo", "couple", "family", "friend"].map((type) => (
                      <div
                        key={type}
                        className={`group-checkbox-item ${createForm.grouptype.includes(type) ? "selected" : ""}`}
                        onClick={() => handleGroupTypeChange(type)}
                      >
                        <input
                          type="checkbox"
                          checked={createForm.grouptype.includes(type)}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span
                          style={{
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          {type}
                        </span>
                      </div>
                    ))}
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>
                    Address <span style={{ color: "#ef4444" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={createForm.location.address}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        location: {
                          ...createForm.location,
                          address: e.target.value,
                        },
                      })
                    }
                    placeholder="Full address"
                    isInvalid={!!validationErrors.address}
                    className="search-input"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.address}
                  </Form.Control.Feedback>
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600 }}>
                        Latitude
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="any"
                        value={createForm.location.lat}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            location: {
                              ...createForm.location,
                              lat: e.target.value,
                            },
                          })
                        }
                        placeholder="e.g. 40.7128"
                        isInvalid={!!validationErrors.lat}
                        className="search-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.lat}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label style={{ fontWeight: 600 }}>
                        Longitude
                      </Form.Label>
                      <Form.Control
                        type="number"
                        step="any"
                        value={createForm.location.lng}
                        onChange={(e) =>
                          setCreateForm({
                            ...createForm,
                            location: {
                              ...createForm.location,
                              lng: e.target.value,
                            },
                          })
                        }
                        placeholder="e.g. -74.006"
                        isInvalid={!!validationErrors.lng}
                        className="search-input"
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.lng}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}

            {/* Step 3: Additional Information */}
            {createStep === 3 && (
              <div>
                <h5
                  style={{
                    marginBottom: "1rem",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  Additional Information
                </h5>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>Tags</Form.Label>
                  <div className="d-flex gap-2 flex-wrap align-items-center mb-2">
                    <Form.Control
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const trimmed = tagInput.trim();
                          if (trimmed) {
                            setCreateForm((prev) => ({
                              ...prev,
                              tags: [...prev.tags, trimmed],
                            }));
                            setTagInput("");
                          }
                        }
                      }}
                      placeholder="Add a tag"
                      className="search-input"
                      style={{ maxWidth: "220px" }}
                    />
                    <Button
                      type="button"
                      variant="primary"
                      style={{ borderRadius: "8px", fontWeight: 600 }}
                      onClick={() => {
                        const trimmed = tagInput.trim();
                        if (trimmed) {
                          setCreateForm((prev) => ({
                            ...prev,
                            tags: [...prev.tags, trimmed],
                          }));
                          setTagInput("");
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" /> Add tag
                    </Button>
                  </div>
                  {createForm.tags.length > 0 && (
                    <div className="tag-chips-wrap">
                      {createForm.tags.map((tag, index) => (
                        <span key={`${tag}-${index}`} className="tag-chip">
                          {tag}
                          <button
                            type="button"
                            className="tag-chip-remove"
                            onClick={() =>
                              setCreateForm((prev) => ({
                                ...prev,
                                tags: prev.tags.filter((_, i) => i !== index),
                              }))
                            }
                            aria-label="Remove tag"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>
                    Phone Number (US)
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    value={createForm.phone}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        phone: formatPhoneNumber(e.target.value),
                      })
                    }
                    placeholder="(123) 456 - 7890"
                    isInvalid={!!validationErrors.phone}
                    className="search-input"
                    maxLength={16}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.phone}
                  </Form.Control.Feedback>
                  <Form.Text style={{ color: "#64748b" }}>
                    10-digit US format
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>Website</Form.Label>
                  <Form.Control
                    type="url"
                    value={createForm.website}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, website: e.target.value })
                    }
                    placeholder="https://example.com or www.example.com"
                    isInvalid={!!validationErrors.website}
                    className="search-input"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.website}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>
                    Booking Link
                  </Form.Label>
                  <Form.Control
                    type="url"
                    value={createForm.bookingLink}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        bookingLink: e.target.value,
                      })
                    }
                    placeholder="https://booking.example.com"
                    isInvalid={!!validationErrors.bookingLink}
                    className="search-input"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.bookingLink}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>
                    Booking Provider
                  </Form.Label>
                  <Form.Select
                    value={createForm.bookingProvider}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        bookingProvider: e.target.value,
                        ...(e.target.value !== "viator" && { viatorProductCode: "" }),
                      })
                    }
                    className="search-input"
                  >
                    <option value="golocal">GoLocal</option>
                    <option value="viator">Viator</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Select "Viator" to link this experience to a Viator product for affiliate booking.
                  </Form.Text>
                </Form.Group>

                {createForm.bookingProvider === "viator" && (
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 600 }}>
                      Viator Product Code *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={createForm.viatorProductCode}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          viatorProductCode: e.target.value,
                        })
                      }
                      placeholder="e.g. 12345P1"
                      isInvalid={!!validationErrors.viatorProductCode}
                      className="search-input"
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.viatorProductCode}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Enter the Viator product code to generate an affiliate booking link.
                    </Form.Text>
                  </Form.Group>
                )}
              </div>
            )}

            {/* Step 4: Hours - Regular or Special Event */}
            {createStep === 4 && (
              <div>
                <h5
                  style={{
                    marginBottom: "1rem",
                    fontWeight: 700,
                    color: "#1e293b",
                  }}
                >
                  Hours
                </h5>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600 }}>Type</Form.Label>
                  <div className="d-flex gap-3 flex-wrap">
                    <Form.Check
                      type="radio"
                      id="hours-regular"
                      name="hoursType"
                      label="Regular opening hours"
                      checked={createForm.hoursType === "regular"}
                      onChange={() =>
                        setCreateForm((p) => ({ ...p, hoursType: "regular" }))
                      }
                    />
                    <Form.Check
                      type="radio"
                      id="hours-special"
                      name="hoursType"
                      label="Special event hours"
                      checked={createForm.hoursType === "special"}
                      onChange={() =>
                        setCreateForm((p) => ({ ...p, hoursType: "special" }))
                      }
                    />
                  </div>
                </Form.Group>
                {(createForm.hoursType === "regular" ||
                  createForm.hoursType === "special") && (
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 600 }}>
                      Business timezone (USA){" "}
                      <span style={{ color: "#ef4444" }}>*</span>
                    </Form.Label>
                    <select
                      className="form-select search-input"
                      value={createForm.timezone ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCreateForm((p) => ({ ...p, timezone: v }));
                      }}
                      aria-invalid={!!validationErrors.timezone}
                    >
                      <option value="">Select timezone...</option>
                      {USA_TIMEZONES.map((tz) => (
                        <option key={tz.id} value={tz.id}>
                          {tz.name}
                        </option>
                      ))}
                    </select>
                    {validationErrors.timezone && (
                      <div className="invalid-feedback d-block">
                        {validationErrors.timezone}
                      </div>
                    )}
                  </Form.Group>
                )}
                {createForm.hoursType === "regular" && (
                  <div style={{ marginTop: "1rem" }}>
                    {[
                      "monday",
                      "tuesday",
                      "wednesday",
                      "thursday",
                      "friday",
                      "saturday",
                      "sunday",
                    ].map((day) => (
                      <div key={day} className="opening-hours-section">
                        <div className="opening-hours-header">
                          <span className="day-label">{day}</span>
                          <Form.Check
                            type="switch"
                            id={`closed-${day}`}
                            label="Closed"
                            checked={createForm.openingHours[day].closed}
                            onChange={(e) => {
                              const c = e.target.checked;
                              setCreateForm((prev) => ({
                                ...prev,
                                openingHours: {
                                  ...prev.openingHours,
                                  [day]: {
                                    ...prev.openingHours[day],
                                    closed: c,
                                    open: c ? "" : prev.openingHours[day].open,
                                    close: c
                                      ? ""
                                      : prev.openingHours[day].close,
                                  },
                                },
                              }));
                            }}
                          />
                        </div>
                        {!createForm.openingHours[day].closed && (
                          <Row>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label style={{ fontSize: "0.875rem" }}>
                                  Open
                                </Form.Label>
                                <Form.Control
                                  type="time"
                                  value={fromIsoToTime(
                                    createForm.openingHours[day].open,
                                  )}
                                  onChange={(e) =>
                                    handleOpeningHourChange(
                                      day,
                                      "open",
                                      e.target.value,
                                    )
                                  }
                                  className="search-input"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label style={{ fontSize: "0.875rem" }}>
                                  Close
                                </Form.Label>
                                <Form.Control
                                  type="time"
                                  value={fromIsoToTime(
                                    createForm.openingHours[day].close,
                                  )}
                                  onChange={(e) =>
                                    handleOpeningHourChange(
                                      day,
                                      "close",
                                      e.target.value,
                                    )
                                  }
                                  className="search-input"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {createForm.hoursType === "special" && (
                  <div style={{ marginTop: "1rem" }}>
                    {validationErrors.specialEventHours && (
                      <div
                        style={{
                          color: "#ef4444",
                          fontSize: "0.875rem",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {validationErrors.specialEventHours}
                      </div>
                    )}
                    {createForm.specialEventHours.map((ev, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          gap: "0.5rem",
                          alignItems: "flex-end",
                          marginBottom: "0.75rem",
                          flexWrap: "wrap",
                        }}
                      >
                        <Form.Group style={{ minWidth: "140px" }}>
                          <Form.Label style={{ fontSize: "0.875rem" }}>
                            Date
                          </Form.Label>
                          <Form.Control
                            type="date"
                            value={ev.date}
                            onChange={(e) =>
                              updateSpecialEvent(idx, "date", e.target.value)
                            }
                            className="search-input"
                          />
                        </Form.Group>
                        <Form.Group style={{ minWidth: "100px" }}>
                          <Form.Label style={{ fontSize: "0.875rem" }}>
                            Start
                          </Form.Label>
                          <Form.Control
                            type="time"
                            value={ev.startTime}
                            onChange={(e) =>
                              updateSpecialEvent(
                                idx,
                                "startTime",
                                e.target.value,
                              )
                            }
                            className="search-input"
                          />
                        </Form.Group>
                        <Form.Group style={{ minWidth: "100px" }}>
                          <Form.Label style={{ fontSize: "0.875rem" }}>
                            End
                          </Form.Label>
                          <Form.Control
                            type="time"
                            value={ev.endTime}
                            onChange={(e) =>
                              updateSpecialEvent(idx, "endTime", e.target.value)
                            }
                            className="search-input"
                          />
                        </Form.Group>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeSpecialEvent(idx)}
                          disabled={createForm.specialEventHours.length <= 1}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={addSpecialEvent}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Add event
                    </Button>
                  </div>
                )}
                {!createForm.hoursType && (
                  <p style={{ color: "#64748b", marginTop: "0.5rem" }}>
                    Choose regular weekly hours or special event dates.
                    Optional.
                  </p>
                )}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer
          style={{ border: "none", padding: "1.25rem", background: "#f8fafc" }}
        >
          <Button
            variant="outline-secondary"
            onClick={handleModalClose}
            disabled={actionLoading || uploadingImages}
            style={{
              borderRadius: "8px",
              fontWeight: 600,
              padding: "0.6rem 1.25rem",
            }}
          >
            Cancel
          </Button>
          <div className="d-flex gap-2">
            {createStep > 1 && (
              <Button
                variant="outline-primary"
                onClick={handleCreatePrev}
                disabled={actionLoading}
                style={{
                  borderRadius: "8px",
                  fontWeight: 600,
                  padding: "0.6rem 1.25rem",
                }}
              >
                Previous
              </Button>
            )}
            {createStep < TOTAL_CREATE_STEPS ? (
              <Button
                style={{
                  background: "#4f46e5",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  padding: "0.6rem 1.25rem",
                }}
                onClick={handleCreateNext}
                disabled={actionLoading || uploadingImages}
              >
                Next
              </Button>
            ) : (
              <Button
                style={{
                  background: "#4f46e5",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  padding: "0.6rem 1.25rem",
                }}
                onClick={handleCreateExperience}
                disabled={actionLoading || uploadingImages}
              >
                {actionLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    Create Experience
                  </>
                )}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PendingExperiencesPage;
